<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { Editor, type JSONContent } from '@tiptap/core';
  import { common, createLowlight } from 'lowlight';
  import 'highlight.js/styles/atom-one-dark.min.css';
  import CodeIcon from '$icons/CodeIcon.svelte';
  import BlockQuoteIcon from '$icons/formatting/BlockQuoteIcon.svelte';
  import BoldIcon from '$icons/formatting/BoldIcon.svelte';
  import ItalicIcon from '$icons/formatting/ItalicIcon.svelte';
  import UnderlineIcon from '$icons/formatting/UnderlineIcon.svelte';
  import ErrorWarningAlertIcon from '$icons/ErrorWarningAlertIcon.svelte';
  import Button from './Button.svelte';
  import { ImageUploader } from '$utils/image';
  import LoaderIcon from '$icons/loaders/LoaderIcon.svelte';
  import { getTipTapProps } from '$lib/utils/tiptap';
  import ImageIcon from '$icons/files/ImageIcon.svelte';
  import { cn } from '$lib/utils/misc';
  import { toastCustom } from '$utils/toasts';

  export let value: JSONContent;
  export let label = '';
  export let name = '';
  export let id = '';
  export let description = '';
  export let optional = false;
  export let errors: string[] = [];
  export let allowImageUpload = false;
  export let allowCodeBlock = true;
  export let allowResize = true;
  export let editor: Editor;

  let element: HTMLElement;
  let isUploadingImage = false;
  let pasteHandler: EventListener;

  const lowlight = createLowlight(common);
  const imageUploader = new ImageUploader();
  const editorStates = {
    bold: false,
    italic: false,
    underline: false,
    blockquote: false,
    codeBlock: false,
    code: false,
  };

  async function uploadImage(file: File) {
    try {
      const response = await imageUploader.compressAndUpload(file);
      editor.chain().focus().deleteSelection().setImage({ src: response.url }).run();
    } catch (error) {
      toastCustom({
        title: 'Failed to upload pasted image',
        description: 'Please try again',
        negative: true,
        position: 'bottom-center',
      });
      console.error('Failed to upload pasted image:', error);
    }
  }

  async function handlePaste(e: ClipboardEvent) {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    e.preventDefault();
    isUploadingImage = true;

    let file: File | null = null;

    // Prefer the HTML payload if present
    const htmlData = clipboardData.getData('text/html');
    if (htmlData) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlData, 'text/html');
      const img = doc.querySelector('img');
      if (img?.src) {
        try {
          const res = await fetch(img.src);
          if (!res.ok) throw new Error('Failed to fetch image');
          const blob = await res.blob();
          const extension = blob.type.split('/')[1] || 'png';
          file = new File([blob], `pasted.${extension}`, { type: blob.type });
        } catch (error) {
          console.error('Error fetching image from HTML:', error);
        }
      }
    }

    // Fallback: directly from clipboard items
    if (!file) {
      const imageItem = Array.from(clipboardData.items).find((item) =>
        item.type.startsWith('image/'),
      );
      if (imageItem) {
        file = imageItem.getAsFile();
      }
    }

    if (file) {
      await uploadImage(file);
    }

    isUploadingImage = false;
  }

  async function handleFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    isUploadingImage = true;
    try {
      await uploadImage(input.files[0]);
    } finally {
      isUploadingImage = false;
      input.value = '';
    }
  }

  function toggleCode() {
    const { from, to } = editor.state.selection;

    if (from === to) {
      editor.chain().focus().toggleCodeBlock().run();
      return;
    }

    if (editor.isActive('codeBlock')) {
      editor.chain().focus().toggleCodeBlock().run();
      return;
    }

    if (editor.isActive('code')) {
      editor.chain().focus().toggleCode().run();
      return;
    }

    let blockCount = 0;
    editor.state.doc.nodesBetween(from, to, (node) => {
      if (node.type.name === 'paragraph' || node.type.name === 'heading') {
        blockCount++;
        if (blockCount > 1) return false;
      }
    });

    if (blockCount > 1) {
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContent({
          type: 'codeBlock',
          content: [
            {
              type: 'text',
              text: editor.state.doc.textBetween(from, to, '\n'),
            },
          ],
        })
        .run();
    } else {
      editor.chain().focus().toggleCode().run();
    }
  }

  function embolden() {
    editor.chain().focus().toggleBold().run();
  }
  function italicize() {
    editor.chain().focus().toggleItalic().run();
  }
  function underline() {
    editor.chain().focus().toggleUnderline().run();
  }
  function blockQuote() {
    editor.chain().focus().toggleBlockquote().run();
  }

  onMount(() => {
    if (allowCodeBlock) {
      lowlight.register(common);
    }
    const { extensions, editorProps } = getTipTapProps();
    editor = new Editor({
      element,
      content: value,
      onTransaction: () => {
        // biome-ignore lint/correctness/noSelfAssign: force re-render so `editor.isActive` works as expected
        editor = editor;
      },
      onUpdate: () => {
        value = editor.getJSON();
      },
      extensions,
      editorProps: {
        ...editorProps,
        attributes: {
          ...editorProps.attributes,
          class: cn(editorProps.attributes.class, 'p-3'),
        },
        handlePaste: (view, event) => {
          // If clipboard has an image, let our custom handler deal with it
          // See PostEditorTextArea.svelte for the custom handler
          const items = event.clipboardData?.items;
          if (items && Array.from(items).some((item) => item.type.startsWith('image/'))) {
            return true;
          }
          return false;
        },
      },
    });

    pasteHandler = ((e: Event) => handlePaste(e as ClipboardEvent)) as EventListener;
    editor.options.element.addEventListener('paste', pasteHandler);

    editor.on('transaction', () => {
      editorStates.bold = editor.isActive('bold');
      editorStates.italic = editor.isActive('italic');
      editorStates.underline = editor.isActive('underline');
      editorStates.blockquote = editor.isActive('blockquote');
      editorStates.codeBlock = editor.isActive('codeBlock');
      editorStates.code = editor.isActive('code');
    });
  });

  onDestroy(() => {
    if (editor) {
      editor.options.element.removeEventListener('paste', pasteHandler);
      editor.destroy();
    }
  });
</script>

<div>
  <label for={id} class="">
    {#if label}
      <p class="font-architekt text-gray-dark-12 mb-1 text-sm font-normal leading-5">
        {label}
        {#if optional}
          <span class="text-gray-dark-10 text-[12px]">(Optional)</span>
        {/if}
      </p>
    {/if}
    {#if description}
      <p class="text-gray-dark-11 mb-2 mt-1 text-xs">
        {description}
      </p>
    {/if}
  </label>
  <div class="border-gray-dark-4 rounded border">
    <div class="border-gray-dark-4 bg-gray-dark-2 flex items-center gap-x-2 border-b p-2">
      <!-- Bold -->
      <Button
        tabindex={-1}
        secondary={editorStates.bold}
        tertiary={!editorStates.bold}
        icon
        class="relative"
        on:click={embolden}><BoldIcon class="h-5 w-5" /></Button
      >
      <!-- Italics -->
      <Button
        tabindex={-1}
        secondary={editorStates.italic}
        tertiary={!editorStates.italic}
        icon
        class="relative"
        on:click={italicize}><ItalicIcon class="h-5 w-5" /></Button
      >
      <!-- Underline -->
      <Button
        tabindex={-1}
        secondary={editorStates.underline}
        tertiary={!editorStates.underline}
        icon
        class="relative"
        on:click={underline}><UnderlineIcon class="h-5 w-5" /></Button
      >
      <!-- Block Quote -->
      <Button
        tabindex={-1}
        secondary={editorStates.blockquote}
        tertiary={!editorStates.blockquote}
        icon
        class="relative"
        on:click={blockQuote}><BlockQuoteIcon class="h-5 w-5" /></Button
      >
      <!-- Code block -->
      {#if allowCodeBlock}
        <Button
          tabindex={-1}
          secondary={editorStates.codeBlock || editorStates.code}
          tertiary={!editorStates.codeBlock && !editorStates.code}
          icon
          class="relative"
          on:click={toggleCode}><CodeIcon class="h-5 w-5" /></Button
        >
      {/if}
      {#if allowImageUpload}
        <div class="relative">
          <Button
            tabindex={-1}
            tertiary
            icon
            on:click={() => document.getElementById('image-upload')?.click()}
          >
            {#if isUploadingImage}
              <LoaderIcon class="h-5 w-5" />
            {:else}
              <ImageIcon class="h-5 w-5" />
            {/if}
          </Button>
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            class="hidden"
            on:change={handleFileChange}
          />
        </div>
      {/if}
    </div>
    <div
      class={cn('relative min-h-[200px] flex-1 overflow-hidden', {
        'resize-y': allowResize,
      })}
    >
      <div bind:this={element} class="absolute inset-0 overflow-y-scroll" />
    </div>
  </div>
  {#if errors?.length > 0}
    {#each errors as error}
      {#if error}
        <div class="text-red-dark-10 mt-2 flex items-center justify-start gap-x-1 gap-y-0.5">
          <ErrorWarningAlertIcon />
          <p class="text-red-dark-10">{error}</p>
        </div>
      {/if}
    {/each}
  {/if}
  <input type="text" {name} bind:value class="sr-only" />
</div>
